import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check as CheckIcon, X as XIcon, Clock as ClockIcon, User as UserIcon, Bank as BankIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { approveReservation, rejectReservation, getReservation, getCommunityReservation } from '@/api/reservations';
import { useUser } from '@/hooks/v2/use-user';
import { toast } from '@/components/core/v2/toaster';
import { AmenityReservation } from '@/types/v2/reservation';

interface ReservationDetailsDialogProps {
  open: boolean;
  reservation: AmenityReservation | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReservationDetailsDialog({
  open,
  reservation,
  onClose,
  onSuccess
}: ReservationDetailsDialogProps): React.JSX.Element {
  const { community, user } = useUser();
  const queryClient = useQueryClient();


  // Check if user is a manager/admin
  const isManager = React.useMemo(() => {
    if (!user || !community) return false;

    // Check account-level roles
    const accountRoles = user.account_roles || [];
    const hasAccountRole = accountRoles.some(role =>
      ['account_owner', 'account_admin', 'account_manager'].includes(role.name)
    );

    // Check community-level roles
    const communityRoles = community.roles || [];
    const hasCommunityRole = communityRoles.includes('community_manager');

    return hasAccountRole || hasCommunityRole;
  }, [user, community]);
  const [rejectReason, setRejectReason] = React.useState('');
  const [showRejectForm, setShowRejectForm] = React.useState(false);

  // Reset reject form state when reservation changes
  React.useEffect(() => {
    setRejectReason('');
    setShowRejectForm(false);
  }, [reservation?.id]);

  // Fetch full reservation details
  const { data: fullReservation, isLoading, error } =  useQuery<AmenityReservation>({
    queryKey: ['reservation', reservation?.id],
    queryFn: async () => await getCommunityReservation(community?.id || '', reservation!.id),
    enabled: open && !!reservation?.id && !!community?.id,
  });

  const approveMutation = useMutation({
    mutationFn: () => approveReservation(community?.id || '', reservationData.data?.id || reservationData.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-slots-all'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      toast.success('Reservation approved successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to approve reservation');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectReservation(community?.id || '', reservationData.data?.id || reservationData.id, { rejected_reason: rejectReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-slots-all'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      toast.success('Reservation rejected successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to reject reservation');
    },
  });

  const handleApprove = () => {
    approveMutation.mutate();
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    rejectMutation.mutate();
  };

  const handleClose = () => {
    setRejectReason('');
    setShowRejectForm(false);
    onClose();
  };

  if (!reservation) {
    return <></>;
  }

  // If we have the basic reservation data but no full data, show what we have
  const reservationData: any = fullReservation || reservation;

  // Show loading state
  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reservation Details</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const isPending = (reservationData.data?.status || reservationData.status) === 'pending';
  const isApproved = (reservationData.data?.status || reservationData.status) === 'approved';
  const isRejected = (reservationData.data?.status || reservationData.status) === 'rejected';

  const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    cancelled: 'default',
    completed: 'info',
    no_show: 'error',
    waitlisted: 'info',
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Reservation Details
        <Chip
          label={(reservationData.data?.status || reservationData.status)?.replace('_', ' ').toUpperCase()}
          size="small"
          color={statusColors[reservationData.data?.status || reservationData.status] || 'default'}
          sx={{ ml: 1 }}
        />
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Amenity Info */}
          <Card variant="outlined">
            <CardContent sx={{ pb: '16px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <BankIcon size={24} weight="duotone" />
                <Box>
                  <Typography variant="h6">{reservationData.data?.amenity?.name || reservationData.amenity?.name || reservationData.amenity_name || 'Unknown Amenity'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reservation #{(reservationData.data?.id || reservationData.id)?.substring(0, 8)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* User Info */}
          {(reservationData.data?.user || reservationData.user) && (
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <UserIcon size={24} weight="duotone" />
                  <Box>
                    <Typography variant="h6">
                      {(reservationData.data?.user || reservationData.user).first_name} {(reservationData.data?.user || reservationData.user).last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(reservationData.data?.user || reservationData.user).email}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Time Info */}
          <Card variant="outlined">
            <CardContent sx={{ pb: '16px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ClockIcon size={24} weight="duotone" />
                <Box>
                  <Typography variant="h6">Reservation Time</Typography>
                  <Typography variant="body1">
                    {dayjs.utc(reservationData.data?.start_time || reservationData.start_time).format('MMM D, YYYY h:mm A')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    to {dayjs.utc(reservationData.data?.end_time || reservationData.end_time).format('h:mm A')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Purpose */}
          {(reservationData.data?.purpose || reservationData.purpose) && (
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="h6" gutterBottom>Purpose</Typography>
                <Typography variant="body2">{reservationData.data?.purpose || reservationData.purpose}</Typography>
              </CardContent>
            </Card>
          )}

          {/* Special Requests */}
          {(reservationData.data?.special_requests || reservationData.special_requests) && (
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="h6" gutterBottom>Special Requests</Typography>
                <Typography variant="body2">{reservationData.data?.special_requests || reservationData.special_requests}</Typography>
              </CardContent>
            </Card>
          )}

          {/* Rejection Reason (if rejected) */}
          {isRejected && (reservationData.data?.rejected_reason || reservationData.rejected_reason) && (
            <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="h6" gutterBottom color="error">Rejection Reason</Typography>
                <Typography variant="body2">{reservationData.data?.rejected_reason || reservationData.rejected_reason}</Typography>
              </CardContent>
            </Card>
          )}

          {/* Reject Form */}
          {showRejectForm && (
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="h6" gutterBottom>Reject Reservation</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Please provide a reason for rejecting this reservation.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rejection Reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this reservation is being rejected..."
                />
              </CardContent>
            </Card>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={approveMutation.isPending || rejectMutation.isPending}>
          Close
        </Button>

        {isPending && (
          <>
            <Button
              onClick={() => setShowRejectForm(!showRejectForm)}
              color="error"
              variant="outlined"
              disabled={approveMutation.isPending || rejectMutation.isPending}
              startIcon={<XIcon size={18} />}
            >
              {showRejectForm ? 'Cancel Reject' : 'Reject'}
            </Button>

            {!showRejectForm && (
              <Button
                onClick={handleApprove}
                color="success"
                variant="contained"
                disabled={approveMutation.isPending || rejectMutation.isPending}
                startIcon={<CheckIcon size={18} />}
              >
                {approveMutation.isPending ? 'Approving...' : 'Approve'}
              </Button>
            )}

            {showRejectForm && (
              <Button
                onClick={handleReject}
                color="error"
                variant="contained"
                disabled={rejectMutation.isPending || rejectMutation.isPending || !rejectReason.trim()}
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Confirm Reject'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
